/**
 * @file        src/pages/flight/InvoiceDoc.jsx
 * @description Admin-side printable TAX INVOICE for a flight booking. Rendered
 *              inside the console (no redirect to the customer site) from the
 *              admin API's own booking details. This is the SAME document as the
 *              customer's copy (each storefront's src/pages/Invoice): From/To
 *              blocks, one itemised line PER PASSENGER (base, OT tax, K3/GST, YQ,
 *              baggage / meal / seat, management fee + its GST, insurance,
 *              processing fee, convenience fee), the Passenger GST
 *              Details table, GST Details, the Amount → Discount → Total Amount
 *              to be Paid summary, the wallet/gateway Payment Details and terms.
 * @author      Kinetika Labs
 * @created     2026-07-10
 * @changelog
 *   - 2026-10-08 Claude — Convenience-fee / Discount lines print the admin-given rule names (fare.convenience_label / fare.discount_label); generic label as fallback.
 *   - 2026-10-06 Claude — Per-passenger table: each passenger's processing fee is inside their Base Fare cell and the Processing Fee column is gone; Convenience Fee column always shows. Row values, Gross and the summary are unchanged.
 *                          Client decision: the Processing fee (our markup) is not
 *                          a line of its own — it is inside the Base fare, and the
 *                          Convenience fee (shown even at ₹0) takes its place.
 *                          Display only: no total or stored figure changes.
 *   - 2026-09-25 Claude — FLIGHT invoice money now comes from `b.fare.summary`
 *                              (the server-composed ledger figures). "Less:
 *                              Discount" is the RECORDED discount rather than
 *                              `gross − total`, and a conditional "Reschedule
 *                              Charge" column was added so a reissued booking's
 *                              table can add up to its own Total. Same change as
 *                              both storefront copies — all three must stay in step
 *                              (§22). See §30.
 *   - 2026-09-25 Claude — The insurance sub-invoice's CGST/SGST cells go
 *     through lib/insuranceFare: an insurer that states a GST total with no
 *     heads printed "CGST 0.00 · SGST 0.00" beside the real GST in the totals
 *     box on the same sheet. IGST is no longer hardcoded to 0 either.
 *   - 2026-09-25 Claude — Flight invoice: Invoice Date is set bold at the Invoice No.'s
 *                          size; Travel Date prints on ONE line (nowrap, weight 5 → 9,
 *                          taken from PAX Name / Sectors / Flight, which wrap — every money
 *                          column still clears the §22 floor); same weights in all three copies.
 *                          Ticket No. reads every source the drawer / e-ticket use
 *                          (per-sector map + list, flat field, admin-keyed ticketed_pax) —
 *                          it read only the flat field and printed "NA".
 *   - 2026-09-23 Claude — "Back to Bookings" returns to the list this document
 *                          was opened from. This route is shared by the B2C and
 *                          the Corporate screen, so Back always landed on the
 *                          B2C list; lib/docOrigin carries which it was.
 *   - 2026-09-22 Claude — Column widths re-weighted: the seven identity columns
 *                          (S No. … Travel Date) WRAP, the eleven money columns
 *                          cannot, so the share moved to the ones that need it
 *                          (each money column 6-6.5 of the normalised total, up
 *                          from 5-6). With the header-wrap fix in admin.css this
 *                          is what makes a six-figure amount fit its column on
 *                          every sheet the document can print on. The two
 *                          storefront copies took the identical weights — the
 *                          three copies of this table must lay out the same.
 *   - 2026-09-22 Claude — PRINT/PDF: same three fixes as the customer copy of this
 *                          invoice (each storefront's pages/Invoice) —
 *                          landscape sheet while the ROUTE is mounted (not the
 *                          ShareEmailModal capture mount), colgroup widths
 *                          normalised to 100 from weights instead of summing to
 *                          131, and money cells right-aligned/nowrap/tabular via
 *                          .doc-num so `word-break:break-word` can no longer
 *                          split an amount across two lines. The table also
 *                          gained the .doc-table-wrap scroll box it never had,
 *                          so a narrow console window scrolls it.
 *   - 2026-09-22 Claude — The seller block prints the issuer's GST STATE CODE and
 *     HSN/SAC (Payment Management -> Tax Management). Both were captured, stored and
 *     shown back to the admin but reached NO invoice: the only SAC on any document
 *     was the hardcoded insurance 998559 in the line-item table, so a tenant that
 *     set 998551 never put it in front of a customer. isSet()-guarded like the
 *     GSTIN/PAN/Reg-No rows beside them, so an unconfigured field prints nothing
 *     rather than an empty label.
 *   - 2026-09-12 Claude — Travel insurance sold WITH the ticket (the international
 *                          add-on) now prints as its OWN insurance invoice UNDER the
 *                          flight invoice — TWO documents, exactly like the customer's
 *                          copy has since 2026-09-11. The "Insurance Fees" column is
 *                          gone from the flight table and the flight total excludes the
 *                          premium, so the two documents add up to the booking's
 *                          total_paid. The insurance invoice prints its OWN number
 *                          (`insurance_invoice_number`, the brand's INS series, issued
 *                          server-side once the policy is Booked) and cites the flight
 *                          invoice beside it; with no number issued — an older booking,
 *                          or a policy the insurer never issued — it cites the flight
 *                          invoice only rather than invent one. "Flight Invoice" /
 *                          "Insurance Invoice" buttons print one document (the other is
 *                          .doc-print-hide for that print); the primary button prints
 *                          both. The admin copy was still ONE document billing the
 *                          premium as a column, so the two copies of one booking
 *                          disagreed on both the document count and the flight total.
 *   - 2026-09-11 Claude — The PAX Name cell lists the passenger's frequent-flyer
 *                          memberships under the name, one per flight — same as
 *                          the customer copy (lib/frequentFlyer).
 *   - 2026-09-10 Claude — PNR No. column filled in. It read a per-PASSENGER
 *                          `pnrByRoute`, which is the CUSTOMER API's shape — the
 *                          admin details payload has never carried it, and its
 *                          own per-sector map (`pnr_by_route`, served since
 *                          today and already read by BookingDetailsDrawer) was
 *                          ignored. Both remaining fallbacks are usually empty
 *                          too (stored legs carry no `pnr`), so the column
 *                          printed blank against a customer copy showing two
 *                          codes. Now takes the union of the sector map, the leg
 *                          / segment PNRs and `pnr_code`, joined in sector order
 *                          exactly like the customer's copy.
 *   - 2026-09-10 Claude — The local PaymentSplitBlock moved to the shared
 *                          components/DocPaymentSplit so the hotel invoice can
 *                          print the identical block. Pure extraction — same
 *                          markup, same labels, same "render nothing when the
 *                          split is unknown" rule.
 *   - 2026-09-09 Claude — Top-left mark always renders a LOGO, via the shared
 *                          components/DocBrandMark (storefront logo → tenant
 *                          admin-portal logo → the green BrandWordmark SVG the
 *                          customer site shows). Neither logo column is
 *                          populated on most tenants, so in practice every
 *                          invoice printed the plain-text COMPANY.legal fallback
 *                          instead of a logo.
 *   - 2026-09-08 Claude — Brand mark added top-left, from the booking's own
 *                          storefront (brands.logo_url) like the e-ticket. The
 *                          invoice printed with no logo at all.
 *   - 2026-09-08 Claude — PARITY again: Discount removed as a table COLUMN and
 *                          moved to a summary box under the total (Amount →
 *                          Less: Discount → Total Amount to be Paid), matching
 *                          the storefront change made the same day. As a column
 *                          it sat on passenger row 1, so a 2-pax ticket read as
 *                          if one traveller got the whole discount — visible on
 *                          booking 478, which printed 2011.00 against row 1 and
 *                          0.00 against row 2. Both summary figures are DERIVED
 *                          (Amount = sum of the printed charge columns; Discount
 *                          = Amount − total_paid) so the box reconciles with the
 *                          table above it and with what was actually charged,
 *                          rather than trusting flightDiscount + couponDiscount,
 *                          which can double-count a single coupon. New
 *                          .doc-totals-grid in styles/admin.css aligns the three
 *                          lines on one left and one right edge.
 *   - 2026-09-07 Claude — PARITY with the customer invoice, which had moved on
 *                          while this copy had not: the two PDFs of ONE booking
 *                          printed different documents. Ported verbatim from
 *                          pages/Invoice/index.jsx —
 *                          (a) one row PER PASSENGER (was one per pax TYPE),
 *                              named "Mr. Soham Deshpande (A)";
 *                          (b) Baggage / Meal / Seat as three columns (was one
 *                              lumped "Meal, Seat & Baggage"), itemised from the
 *                              stored ancillary split with the legacy
 *                              combined-only fallback;
 *                          (c) Insurance Fees + Convenience Fee columns, and
 *                              charge columns that render only when actually
 *                              levied, like the reference invoice;
 *                          (d) Processing Fee = fd.markup + transaction fee,
 *                              APPORTIONED across passengers (it was
 *                              back-solved as total − grandTotal and printed
 *                              whole on row 1);
 *                          (e) sectors / flights across EVERY segment, so a
 *                              connection reads "NMI-BLR-CCU" / "IX-1347,
 *                              IX-1537" instead of the collapsed "NMI-CCU";
 *                          (f) the Passenger GST Details table;
 *                          (g) Gross → Less Flight Commission → Add Flight TDS
 *                              → Net Amount replaced by a single Total Amount
 *                              (the reseller commission layer was retired
 *                              server-side in CR E8; b2b_commission is always 0,
 *                              so those lines printed a permanent 0.00);
 *                          (h) the wallet/gateway Payment Details block;
 *                          (i) the circular "VERIFIED" stamp removed — dropped
 *                              from the customer copy on 2026-08-05 because
 *                              nothing verifies it and a self-issued "verified"
 *                              mark on a tax document is a claim best not made.
 *                          The From block stays tenant-resolved (useCompany) —
 *                          see the 2026-08-10 entry below; it must never be
 *                          hardcoded to match one tenant's copy.
 *   - 2026-08-17 Claude — Invoice No. now reads `invoice_number` from the details
 *                          API (the issued series number) instead of deriving one
 *                          from the booking reference. The admin and customer
 *                          copies of the same invoice printed DIFFERENT numbers,
 *                          neither matching the configured series.
 *   - 2026-08-04 Claude — CR D2: accepts an optional `bookingIdOverride` prop
 *                          that short-circuits useParams() — see TicketDoc.jsx
 *                          for why (ShareEmailModal off-screen PDF capture).
 *   - 2026-07-10 Claude — Initial (admin-native invoice; replaces the deep link).
 *   - 2026-08-10 Claude — Issuer is fully tenant-scoped: the verification stamp
 *                          no longer hardcodes "NEON HOLIDAYS" (it renders
 *                          COMPANY.name), so a Beyond invoice stops stamping
 *                          Neon's mark. See lib/useCompany.js + migration 051.
 *   - 2026-08-10 Claude — Status gate via docAccess(): an invoice exists only
 *                          for a confirmed or cancelled booking (client-approved
 *                          rule) — pending / cancellation-pending bookings get a
 *                          clear "not available" notice instead of a printable
 *                          invoice.
 *   - 2026-09-15 Claude — The seller block now prints the issuer's GSTIN, PAN and
 *     registration number, and the Terms list appends whatever the tenant
 *     configured (Payment Management → Tax Management, migration 056). The
 *     supplier GSTIN was absent from this document entirely — a tax invoice
 *     without one is not a valid tax invoice, and the customer cannot claim
 *     input credit against it. Each line renders only when configured: a seller
 *   - 2026-09-22 Claude — The embedded INSURANCE sub-document's Terms list now
 *     appends the tenant's configured clauses too. The flight document above it
 *     has since 2026-09-15; the second document on the same sheet did not, so
 *     one booking printed the tenant's terms on one of its two invoices.
 *     who is not GST-registered issues a bill of supply with no GSTIN.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { backToList } from '../../lib/docOrigin';
import { useQuery } from '@tanstack/react-query';
import Icon from '../../components/Icon';
import { getBookingDetails } from '../../api/flightBookings';
import { docAccess } from './cells';
import DocBrandMark from '../../components/DocBrandMark';
import DocPaymentSplit from '../../components/DocPaymentSplit';
import { useCompany } from '../../lib/useCompany';
import { isSet, extraTerms } from '../../lib/company';
import { insuranceGstSplit } from '../../lib/insuranceFare';
import { ffLabels } from '../../lib/frequentFlyer';

// SAC for "other insurance services" — what the premium is billed under, and the
// same code the customer's copy of this invoice prints.
const INSURANCE_SAC = '998559';

const n2 = (v) => (Number(v) || 0).toFixed(2);
const rupee = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const r2m = (v) => Math.round((Number(v) || 0) * 100) / 100;
const fmtDate = (v) => {
  const d = v ? new Date(v) : new Date();
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtDT = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

/**
 * Split the stored fareDetail tax codes into the invoice's columns: K3/GST = the
 * airline GST codes, YQ = fuel surcharge, everything else lands in OT Tax.
 */
function splitTaxes(taxItems = []) {
  let ot = 0; let k3 = 0; let yq = 0;
  for (const t of taxItems) {
    const code = String(t.code || '').toUpperCase();
    const amt = Number(t.amount) || 0;
    if (/GST|K3/.test(code)) k3 += amt;
    else if (code === 'YQ') yq += amt;
    else ot += amt;
  }
  return { ot, k3, yq };
}

// Split a booking-level total into `count` equal parts, the LAST part absorbing
// the rounding remainder so the parts sum to EXACTLY the total. On an invoice the
// line items must add up to the stated total, so a lost paisa is a real defect.
const splitEven = (total, count) => {
  const c = Math.max(1, count);
  const t = Number(total) || 0;
  const each = r2m(t / c);
  const arr = Array.from({ length: c }, () => each);
  arr[c - 1] = r2m(t - each * (c - 1));
  return arr;
};

// Split an authoritative total across rows in proportion to per-row weights (e.g.
// each traveller's own add-on picks). The remainder lands on the last row that
// actually carries weight, so a traveller who bought nothing still shows 0.00.
// Zero total → all zeros; total but no weights → everything on the first row (the
// behaviour for records with no per-pax detail).
const splitByWeight = (total, weights) => {
  const c = Math.max(1, weights.length);
  const t = Number(total) || 0;
  const w = weights.map((x) => Math.max(0, Number(x) || 0));
  const sum = w.reduce((s, x) => s + x, 0);
  if (!t) return Array.from({ length: c }, () => 0);
  if (sum <= 0) {
    const a = Array.from({ length: c }, () => 0);
    a[0] = r2m(t);
    return a;
  }
  const arr = w.map((x) => r2m((t * x) / sum));
  const last = w.reduce((idx, x, i) => (x > 0 ? i : idx), 0);
  arr[last] = r2m(t - arr.reduce((s, v, i) => (i === last ? s : s + v), 0));
  return arr;
};

/**
 * From (the configured issuer) / To (the booker's contact). Shared by the flight
 * invoice and the insurance invoice below it so the two documents of ONE booking
 * can never drift apart — the same extraction the customer copy made when it
 * gained a second document.
 */
function PartiesBlock({ b, company }) {
  return (
    <div className="doc-parties">
      <div>
        <p className="doc-h">From</p>
        <p><b>{company.legal}</b></p>
        <p><b>Address</b>: {company.address}</p>
        <p><b>Email</b>: {company.email}</p>
        <p><b>Phone</b>: {company.phone}</p>
        {isSet(company.gstin) ? <p><b>GSTIN</b>: {company.gstin}</p> : null}
        {isSet(company.pan) ? <p><b>PAN</b>: {company.pan}</p> : null}
        {isSet(company.registrationNo) ? <p><b>Reg. No</b>: {company.registrationNo}</p> : null}
        {isSet(company.gstStateCode) ? <p><b>GST State Code</b>: {company.gstStateCode}</p> : null}
        {isSet(company.hsn) ? <p><b>HSN/SAC</b>: {company.hsn}</p> : null}
      </div>
      <div className="doc-to">
        <p className="doc-h">To</p>
        <p><b>Email</b>: {b.contact?.email || b.customer_email || '—'}</p>
        <p><b>Phone</b>: {b.contact?.phone || '—'}</p>
      </div>
    </div>
  );
}

/**
 * Invoice for the travel-insurance policy sold WITH a flight (the embedded
 * international add-on), printed under the flight invoice as its own document —
 * the same split the customer's copy prints (its FlightInsuranceInvoiceDoc).
 *
 * `amount` is what the customer was actually CHARGED for the cover
 * (fare.detail.ancillary.travelInsurance); the flight invoice leaves it out, so
 * the two documents together add up to the booking's total_paid.
 *
 * Its own number (`b.insurance_invoice_number`, the brand's insurance series) is
 * allocated server-side once the insurer has issued the policy. Without one — a
 * booking from before that existed, or a policy never issued — this document
 * cites the flight invoice it belongs to rather than inventing a number.
 */
function FlightInsuranceInvoiceDoc({ b, company, amount, flightInvoiceNo, invoiceDate }) {
  const ins = b.insurance || {};
  // The insurer's OWN split, the codes a standalone policy carries too: SP is the
  // premium, SPGST the tax on it, CGST/SGST that tax's halves — not extra charges.
  // Absent when no policy was issued; only the charged total is stated then, never
  // a guessed breakdown on a tax document.
  const fare = ins.policy?.fare || {};
  const { premium, gst: premiumGst, cgst, sgst, igst, hasSplit: hasFareSplit } = insuranceGstSplit(fare);
  // Our margin over the supplier's premium + its GST, so the lines reconcile to
  // exactly `amount` instead of leaving an unexplained residual.
  const processing = hasFareSplit ? Math.max(0, r2m(amount - premium - premiumGst)) : 0;

  // One line per insured traveller. The CHARGED total is apportioned by each
  // traveller's own supplier premium, so the column sums to exactly `amount` even
  // though our margin is booking-level.
  const travellers = Array.isArray(ins.policy?.travellers) ? ins.policy.travellers : [];
  const amountSplit = splitByWeight(amount, travellers.map((t) => t.premium));
  const lines = travellers.length
    ? travellers.map((t, i) => ({ name: t.name, policyId: t.policyId, amount: amountSplit[i] ?? 0 }))
    : [{ name: null, policyId: null, amount }];

  // fmtDate() falls back to TODAY for an empty value — right for an invoice date,
  // wrong for a cover date we simply do not have.
  const coverDate = (v) => (v ? fmtDate(v) : '—');
  const planFacts = [
    ['Insurer', ins.insurer],
    ['Plan', ins.tier],
    ['Cover', ins.cover],
    ['Cover Period', (ins.coverFrom || ins.coverTo) ? `${coverDate(ins.coverFrom)} – ${coverDate(ins.coverTo)}` : null],
  ].filter(([, v]) => v);

  return (
    <>
      <DocBrandMark brandCode={b.brand} className="doc-brand-logo" />
      <h1 className="doc-title">INSURANCE INVOICE</h1>

      <div className="doc-meta">
        <p>No.: {b.booking_reference || '—'}</p>
        <p>Invoice Date: {invoiceDate}</p>
      </div>
      {b.insurance_invoice_number ? (
        <>
          <p className="doc-invno">Invoice No.: {b.insurance_invoice_number}</p>
          <p className="doc-subno">Against Flight Invoice No.: {flightInvoiceNo}</p>
        </>
      ) : (
        <p className="doc-invno">Against Flight Invoice No.: {flightInvoiceNo}</p>
      )}
      {ins.bookingId && <p className="doc-subno">Insurance Booking ID: {ins.bookingId}</p>}

      <hr className="doc-hr" />

      <PartiesBlock b={b} company={company} />

      {planFacts.length > 0 && (
        <p className="doc-facts">
          {planFacts.map(([k, v], i) => (
            <span key={k}>{i ? ' · ' : ''}<b>{k}</b>: {v}</span>
          ))}
        </p>
      )}

      <table className="doc-table doc-gst">
        <colgroup>{[6, 12, 20, 38, 24].map((w, i) => <col key={i} style={{ width: `${w}%` }} />)}</colgroup>
        <thead>
          <tr>{['S No.', 'HSN/SAC', 'Policy No.', 'PAX Name', 'Amount (Incl. Taxes)'].map((h) => <th key={h}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {lines.map((t, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td>{INSURANCE_SAC}</td>
              {/* No policy number means the insurer never issued for this
                  traveller — say so rather than print a blank cell. */}
              <td>{t.policyId || 'Not issued'}</td>
              <td>{t.name || '—'}</td>
              <td>{n2(t.amount)}</td>
            </tr>
          ))}
          <tr>
            <td /><td /><td />
            <td><b>Total</b></td>
            <td><b>{n2(amount)}</b></td>
          </tr>
        </tbody>
      </table>

      <div className="doc-summary">
        {/* GST only when the insurer's split exists — printing 0.00 for tax we
            cannot see would be a false statement on a tax document. */}
        {hasFareSplit ? (
          <div>
            <p className="doc-h">GST Details :</p>
            <table className="doc-table doc-gst">
              <thead><tr><th>Description</th><th>CGST @9%</th><th>SGST @9%</th><th>IGST @18%</th></tr></thead>
              <tbody>
                <tr>
                  <td>Premium GST</td>
                  <td>{n2(cgst)}</td>
                  <td>{n2(sgst)}</td>
                  <td>{n2(igst)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : <div />}
        <div className={`doc-totals${hasFareSplit ? '' : ' doc-total-only'}`}>
          {hasFareSplit && (
            <>
              <div className="doc-trow"><span>Premium</span><span>{n2(premium)}</span></div>
              <div className="doc-trow"><span>GST</span><span>{n2(premiumGst)}</span></div>
              {/* Sub-rupee noise is rounding, not a fee. */}
              {processing >= 0.5 && (
                <div className="doc-trow"><span>Processing fee</span><span>{n2(processing)}</span></div>
              )}
            </>
          )}
          <div className="doc-trow doc-net"><b>Total Amount</b><b>{rupee(amount)}</b></div>
          <p className="doc-paid-note">Paid as part of flight booking {b.booking_reference || '—'}.</p>
        </div>
      </div>

      <hr className="doc-hr" />

      <div className="doc-terms">
        <p className="doc-h">Terms &amp; Conditions :</p>
        <ul>
          <li>All cases &amp; disputes are subject to {company.jurisdiction} jurisdiction only.</li>
          <li>Insurance claims, coverage and exclusions are governed exclusively by the insurer’s policy wording.</li>
          <li>Refunds &amp; cancellations are subject to insurer approval.</li>
          {/* Tenant-configured terms (Tax Management), one <li> per line. */}
          {extraTerms(company.terms).map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      </div>

      <p className="doc-foot">This is a Computer Generated Invoice, No Signature required.</p>
    </>
  );
}

/**
 * Print the invoice on a LANDSCAPE sheet while the invoice ROUTE is mounted.
 *
 * The charge table carries 16-18 columns. An A4 PORTRAIT content box is 186mm
 * ≈ 703px, so `table-layout: fixed` handed each column ~40px — narrower than the
 * amounts in it — and `word-break: break-word` then split the amounts
 * themselves, printing "8000.0" over "0" (2026-09-22). Landscape gives 273mm
 * ≈ 1032px, which is what the column weights are now sized against.
 *
 * `@page` takes no selector, so it cannot be scoped to one component in
 * admin.css: the rule is INJECTED on mount and removed on unmount. Appended to
 * <head> last, it also outranks admin.css's `@page { margin: 12mm }`, which
 * stays portrait for the e-ticket and the hotel documents. NOT applied to the
 * ShareEmailModal's off-screen capture mount (`bookingIdOverride`) — that is an
 * html2canvas image, and it must not change what an unrelated page would print.
 */
function useLandscapePrint(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const el = document.createElement('style');
    el.setAttribute('data-print-sheet', 'invoice-landscape');
    el.textContent = '@page { size: A4 landscape; margin: 10mm; }';
    document.head.appendChild(el);
    return () => el.remove();
  }, [enabled]);
}

export default function InvoiceDoc({ bookingIdOverride } = {}) {
  const { id: routeId } = useParams();
  const id = bookingIdOverride ?? routeId;
  const navigate = useNavigate();
  // Which list to return to — this document route is shared by the B2C and the
  // Corporate screen (lib/docOrigin).
  const { search } = useLocation();
  const COMPANY = useCompany();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['flight-booking-details', Number(id)],
    queryFn: () => getBookingDetails(id),
  });
  const b = data?.data;

  useEffect(() => { if (b) document.title = `Invoice ${b.booking_reference} · ${COMPANY.name}`; }, [b]);

  // Single-document print on a booking that carries BOTH invoices: 'flight' or
  // 'insurance' marks the OTHER document .doc-print-hide for one print, then
  // resets on afterprint. null = print everything. The title doubles as the
  // browser's suggested "Save as PDF" file name. Declared before the early
  // returns below, as every hook here must be.
  const [printOnly, setPrintOnly] = useState(null);
  useEffect(() => {
    if (!printOnly) return undefined;
    const prevTitle = document.title;
    document.title = `${printOnly === 'insurance' ? 'Insurance' : 'Flight'} Invoice ${b?.booking_reference || ''}`;
    const done = () => { document.title = prevTitle; setPrintOnly(null); };
    window.addEventListener('afterprint', done, { once: true });
    window.print();
    return () => window.removeEventListener('afterprint', done);
  }, [printOnly, b]);

  // The insurance-only download is a 5-column document and stays portrait.
  useLandscapePrint(!bookingIdOverride && printOnly !== 'insurance');

  if (isLoading) return <div className="panel glass"><div className="empty">Preparing invoice…</div></div>;
  if (isError || !b) return <div className="panel glass"><div className="empty">Could not load this booking.</div></div>;

  // An invoice exists only for a confirmed or cancelled booking (client-approved
  // rule) — gated here too, not just at the buttons, so a pasted URL can't
  // print one for a booking that was never paid for.
  if (!docAccess(b.booking_status).invoice) {
    return (
      <div className="panel glass">
        <div className="empty">
          An invoice is only issued once a booking is confirmed — this booking is “{b.booking_status || 'not confirmed'}”.
          <div style={{ marginTop: 12 }}>
            <button className="btn ghost" onClick={() => navigate(backToList(search, '/flight/bookings', '/flight/corporate-bookings'))}>
              <Icon name="chev" style={{ transform: 'rotate(180deg)' }} /> Back to Bookings
            </button>
          </div>
        </div>
      </div>
    );
  }

  const ref = b.booking_reference || '';
  const total = Number(b.fare?.total_paid) || 0;
  // The ISSUED number from the configured series (Payment → Invoice Number
  // Series), as stored on the booking's invoice row — the SAME number the
  // customer's copy prints. The derived string is a legacy fallback only, for
  // bookings with no invoice row; it ignored the configured prefix/padding/FY
  // segment and, being ref-derived, could repeat across bookings.
  const invoiceNo = b.invoice_number
    || `FLT-${String(ref).replace(/\D/g, '').slice(-10) || '0'}`;
  const fd = b.fare?.detail || null;
  const invoiceDate = fmtDate(b.created_at);

  // Travel insurance bought WITH this ticket (the international add-on) gets its
  // OWN invoice under the flight one, so the flight invoice leaves it out: its
  // table and total cover the ticket alone, and the two documents add up to
  // total_paid. Only a stored fareDetail carries the charged premium separately —
  // a booking without one keeps the single combined invoice it always printed.
  // THE fare summary the server composed from this booking's own `flight_bookings`
  // columns, by the module booking_backend builds the CUSTOMER's copy from
  // (admin_backend/src/utils/flightFareSummary.js, its deliberate mirror). Every
  // money line on this invoice comes from it; `fd` still supplies the
  // per-passenger and per-tax-code DETAIL, but no longer an amount. See §30.
  const svc = b.fare?.summary || null;
  // May this document print the supplier's per-CODE tax split? The server publishes
  // it only when the codes sum to the recorded `gst_amount` AND the breakdown's base
  // matches `base_fare`; otherwise it lumps the tax and logs the mismatch, and this
  // table has to make the same choice or its columns stop adding up to its Total.
  const taxSplitTrusted = !svc || svc.tax?.reconciled !== false;
  const insAmt = r2m(svc ? svc.insurance : fd?.ancillary?.travelInsurance);
  const hasInsInvoice = insAmt > 0;
  const flightTotal = r2m(total - insAmt);

  // Sectors + flights across EVERY physical segment, not just each leg's
  // origin→final. A connecting leg NMI→BLR→CCU must read "NMI-BLR-CCU" with both
  // flights "IX-1347, IX-1537", not the collapsed "NMI-CCU" and one code.
  const legSegs = (l) => (Array.isArray(l.segments) && l.segments.length ? l.segments : null);
  const sectors = (b.legs || []).map((l) => {
    const segs = legSegs(l);
    if (segs) return [segs[0].fromCode, ...segs.map((s) => s.toCode)].filter(Boolean).join('-');
    return `${l.fromCode || ''}-${l.toCode || ''}`;
  }).filter((s) => s && s !== '-').join(', ') || '—';
  const flights = (b.legs || []).flatMap((l) => {
    const segs = legSegs(l);
    if (segs) return segs.map((s) => `${s.airlineCode || ''}-${s.flightNo || ''}`.replace(/^-|-$/g, ''));
    return [l.code].filter(Boolean);
  }).filter(Boolean).join(', ') || '—';
  const travelDate = fmtDT(b.legs?.[0]?.depISO || b.journey_date);

  const paxAll = b.passengers || [];
  // The airline's ticket number(s) and the sector PNRs, per passenger — issued at
  // ticketing, so absent on a booked-not-yet-ticketed order (and in the sandbox).
  // "NA" then, never an invented number.
  //
  // A traveller's number lives in any of the places the booking drawer and the
  // e-ticket already read (ticketNoOf): the supplier's per-sector map, the per-
  // sector list, the flat field, and last the number an admin keyed by hand into
  // flight_passengers (`ticketed_pax`). This read only the flat `ticketNumber`,
  // which the admin payload rarely fills — so the invoice printed "NA" beside a
  // drawer and an e-ticket that both showed the number. Distinct values joined:
  // a single-PNR multi-sector journey repeats one number on every sector.
  const ticketsOfPax = (p) => {
    if (!p) return '';
    const tk = (b.ticketed_pax || [])[paxAll.indexOf(p)] || {};
    return [...new Set([
      ...Object.values(p.ticketByRoute || {}),
      ...(Array.isArray(p.tickets) ? p.tickets : []),
      p.ticketNumber,
      tk.ticket_number,
    ].filter(Boolean))].join(', ');
  };
  const ticketOfType = (type) => [...new Set(paxAll
    .filter((p) => (p.type || 'ADULT') === type)
    .map(ticketsOfPax)
    .filter(Boolean))]
    .join(', ');
  // Every distinct PNR on the booking, in sector order. The airline issues one
  // per OPERATED FLIGHT, so a connection across two carriers has two — the
  // customer's copy prints them joined ("TESTPNR, 6VC6JP") and this must match.
  //
  // `b.pnr_by_route` is the admin API's own per-sector map (the supplier's,
  // persisted at book time). The per-PASSENGER `pnrByRoute` this used to read is
  // the CUSTOMER API's shape — the admin payload has never carried it, so the
  // whole expression collapsed to the fallbacks, and stored legs carry no `pnr`
  // either, leaving only `pnr_code`. That column therefore printed blank on any
  // booking whose `bookings.pnr_code` was never written back, which is why the
  // admin invoice showed nothing where the customer's showed two codes.
  const allPnrs = (() => {
    const fromMap = Object.values(b.pnr_by_route || {}).filter(Boolean);
    const fromLegs = (b.legs || []).flatMap((l) => [
      ...(legSegs(l) || []).map((s) => s.pnr),
      l.pnr,
    ]).filter(Boolean);
    const fromPax = paxAll.flatMap((p) => Object.values(p?.pnrByRoute || {})).filter(Boolean);
    return [...new Set([...fromMap, ...fromLegs, ...fromPax, b.pnr_code].filter(Boolean))];
  })();
  const pnrOfType = (type) => {
    // A per-passenger map wins when one is present (it is that traveller's own
    // set); otherwise every traveller on the booking shares the booking's PNRs.
    const p = paxAll.find((x) => (x.type || 'ADULT') === type);
    const own = [...new Set(Object.values(p?.pnrByRoute || {}).filter(Boolean))];
    return (own.length ? own : allPnrs).join(', ') || 'NA';
  };

  let rows = [];
  let gstRow = { cgst: 0, sgst: 0, igst: 0 };
  if (fd) {
    const { ot, k3, yq } = splitTaxes(fd.taxItems);
    // Per-type add-on split for the Baggage / Meal / Seat columns. The backend
    // stores these SEPARATELY (ancillary.seat / .baggage / .meal — set at book
    // time and grown by each post-booking SSR add), so itemise them instead of
    // sweeping everything into Baggage. Baggage carries any baggage-protection.
    // Older bookings that stored ONLY the combined `mealSeatBaggage` fall back to
    // the previous behaviour: the whole lump lands in Baggage.
    const anc = fd.ancillary || {};
    const bagAmt = Number(anc.baggage) || 0;
    const seatAmt = Number(anc.seat) || 0;
    const bagProt = Number(anc.baggageProtection) || 0;
    const combined = Number(anc.mealSeatBaggage) || 0;
    // Meal: prefer the stored per-type figure. Bookings written before meal was
    // stored separately leave the meal amount trapped in the combined total — so
    // when there's no `meal` field but the record DID split seat/baggage, recover
    // the meal as the residual (combined − baggage − seat − protection). Without
    // this the meal drops off the invoice and the columns no longer sum to the
    // fare. A truly old combined-only row keeps the whole lump in Baggage.
    let mealAmt = Number(anc.meal) || 0;
    if (anc.meal == null && (bagAmt > 0 || seatAmt > 0) && combined > 0) {
      mealAmt = Math.max(0, r2m(combined - bagAmt - seatAmt - bagProt));
    }
    const hasSplit = bagAmt > 0 || seatAmt > 0 || mealAmt > 0;
    const baggageCol = (hasSplit ? bagAmt : combined) + bagProt;
      // ── The add-on columns must sum to the RECORDED add-on total ────────────
    // The taxes above follow the ledger when the stored breakdown disagrees; the
    // add-ons must too, or `Gross − Discount` stops equalling `Total` with nothing
    // on the sheet naming the gap. The old `Math.max(0, gross − total)` absorbed
    // that as a phantom discount; now it would simply be visible bad arithmetic.
    //
    // It is reachable. A paid SSR chosen for an INFANT is skipped by
    // `attachBaggageSsr` (`ssrEligible`), so it never reaches `tripjack_fare` and
    // is absorbed by `markup` — while `mealSeatBaggage` was built from the
    // un-filtered selection and still contains it. The ledger reconciles, so the
    // rest of the document is right; only these three columns would double-count.
    // Re-apportion the recorded total by the same relative weights; splitByWeight
    // distributes an AUTHORITATIVE figure, so the columns sum to it exactly.
    let bagCol = baggageCol;
    let mealCol = mealAmt;
    let seatCol = seatAmt;
    if (svc && Math.abs(r2m(bagCol + mealCol + seatCol) - r2m(svc.addons)) > 0.01) {
      const parts = splitByWeight(r2m(svc.addons), [bagCol, mealCol, seatCol]);
      // All three at zero carries no weight to split by — the whole recorded
      // figure then lands in Baggage, which is the same lump the combined-only
      // branch above already uses.
      const anyWeight = bagCol > 0 || mealCol > 0 || seatCol > 0;
      bagCol = anyWeight ? (parts[0] ?? 0) : r2m(svc.addons);
      mealCol = anyWeight ? (parts[1] ?? 0) : 0;
      seatCol = anyWeight ? (parts[2] ?? 0) : 0;
    }
    // Our margin, billed as the Processing Fee — computed server-side from the
    // ticketed fare (customer's ticket − the supplier's). It used to be derived
    // here as `total − grandTotal`, which silently swept every unexplained rupee
    // into this column.
    // The amount RECORDED on the booking (`flight_bookings.processing_fee`), not
    // `fd.markup` — which was `ticketsTotal − supplierTotal`, a subtraction between
    // two stores read at two different moments (§30).
    const processing = svc ? r2m(svc.processing) : (Number(fd.markup) || 0);
    const txnFee = svc ? r2m(svc.transaction) : (Number(b.fare?.transaction_fee) || 0);
    const convFee = svc ? r2m(svc.convenience) : (Number(b.fare?.convenience_fee) || 0);
    // A reschedule grew `total_paid` by its own charge and this invoice had no
    // column for it, so the printed columns fell SHORT of the Total below them —
    // silently, because the shortfall showed up as a negative "discount" that
    // `Math.max(0, …)` threw away.
    const reissueCharge = svc ? r2m(svc.reissue) : 0;
    // What actually came off the price — the two ledger COLUMNS, summed. The old
    // form preferred `fd.checkout`, so a blob carrying zeros beat a real ₹2.00 in
    // `discount_amount`; this console then printed the difference as "Other
    // adjustments −₹2.00" while the storefront printed "Discount −₹2.00".
    const discount = svc
      ? r2m(svc.discount)
      : ((Number(fd.checkout?.flightDiscount) || 0)
        + (Number(b.fare?.coupon_discount) || Number(fd.checkout?.couponDiscount) || 0));
    const perPax = fd.perPax?.length ? fd.perPax : [{ type: 'ADULT', count: 1, base: fd.base, taxes: fd.taxTotal }];

    // ---- one row PER PASSENGER, matching the supplier's AGENCY invoice --------
    // fd.perPax is keyed by pax TYPE, and its base/taxes are ALREADY the type
    // TOTAL (flightFareDetail.js does `pp.base += bf * count`), so there is no
    // second `* count` here. Per person = type total / count, which is EXACT
    // because airline pricing is uniform within a pax type.
    const paxRows = [];
    perPax.forEach((pp) => {
      const type = pp.type || 'ADULT';
      const count = Math.max(1, Number(pp.count) || 1);
      const members = paxAll.filter((p) => (p.type || 'ADULT') === type);
      const slots = members.length || count;
      // Prefer this pax type's REAL per-code taxes when the booking stored them
      // (perPax[].taxItems). Apportioning by share of total tax prints tax the
      // airline never charged — an INFANT is exempt from K3/YQ and billed OT
      // only, yet a proportional split gives it a slice of both. Only bookings
      // made before the breakdown was stored still apportion.
      const own = Array.isArray(pp.taxItems) && pp.taxItems.length ? splitTaxes(pp.taxItems) : null;
      const share = fd.taxTotal > 0 ? (Number(pp.taxes) || 0) / fd.taxTotal : 1 / perPax.length;
      const baseSplit = splitEven(Number(pp.base) || 0, slots);
      const otSplit = splitEven(own ? own.ot : ot * share, slots);
      const k3Split = splitEven(own ? own.k3 : k3 * share, slots);
      const yqSplit = splitEven(own ? own.yq : yq * share, slots);
      for (let j = 0; j < slots; j += 1) {
        const p = members[j];
        paxRows.push({
          type,
          paxIndex: p ? paxAll.indexOf(p) : -1,
          // Frequent flyer, one label per flight — printed under the name.
          ff: p ? ffLabels(p) : [],
          pax: p
            ? `${p.title ? `${p.title}. ` : ''}${p.firstName || ''} ${p.lastName || ''} (${type[0]})`.replace(/\s+/g, ' ').trim()
            : `${type} ${j + 1}`,
          ticketNo: ticketsOfPax(p) || ticketOfType(type) || 'NA',
          pnr: pnrOfType(type),
          base: baseSplit[j] ?? 0,
          ot: otSplit[j] ?? 0,
          k3: k3Split[j] ?? 0,
          yq: yqSplit[j] ?? 0,
        });
      }
    });

    // ── The supplier columns must describe the fare we RECORDED ───────────────
    // `base` / `ot` / `k3` / `yq` / Management Fee / its Tax all come from the
    // stored breakdown. The server publishes that code split ONLY when it sums to
    // the recorded `gst_amount` AND its base matches `base_fare`; otherwise it
    // lumps the tax and logs FLIGHT_FARE_TAX_SPLIT_MISMATCH (§13, §30). This table
    // has to make the SAME choice, or it prints one breakdown's columns beside the
    // other's Discount and stops adding up to its own Total — silently, since the
    // shortfall would show only as a discount that is not there. Keep every
    // passenger ROW and re-apportion the LEDGER's base and one lumped tax across
    // them; `splitByWeight` distributes an AUTHORITATIVE total, so each column still
    // sums to exactly what was recorded.
    const splitTrusted = taxSplitTrusted;
    if (!splitTrusted) {
      const baseByLedger = splitByWeight(r2m(svc.base), paxRows.map((r) => r.base));
      const taxByLedger = splitByWeight(r2m(svc.taxes), paxRows.map((r) => r.ot + r.k3 + r.yq));
      paxRows.forEach((r, i) => {
        r.base = baseByLedger[i] ?? 0;
        r.ot = taxByLedger[i] ?? 0;   // one lumped "Taxes and Fees"; the column relabels itself
        r.k3 = 0;
        r.yq = 0;
      });
    }

    // Per-traveller add-on weights from `selected_addons`, which is paxIndex-
    // tagged (persisted at book time as what the customer was CHARGED). Where the
    // airline later dropped an add-on the refund is handled by
    // payment_reconciliation — the invoice must still show what was billed.
    const weightOf = (kind) => paxRows.map((r) => (r.paxIndex < 0 ? 0 : (b.selected_addons || [])
      .filter((a) => a.type === kind && (Number(a.paxIndex) || 0) === r.paxIndex)
      .reduce((s, a) => s + (Number(a.amount) || 0), 0)));
    // Distribute the AUTHORITATIVE stored totals by those weights, so each column
    // still sums to exactly what was charged even when a post-booking SSR add
    // moved the total. No per-pax detail → all on the first row.
    const bagSplit = splitByWeight(bagCol, weightOf('Baggage'));
    const mealSplit = splitByWeight(mealCol, weightOf('Meal'));
    const seatSplit = splitByWeight(seatCol, weightOf('Seat'));
    // Management Fee + its GST are real per-passenger supplier components (TAF
    // MF / MFT, levied × count), so they split evenly — the supplier's invoice
    // shows the same figure a head.
    // Part of the very split judged above — when that is untrusted their money is
    // already inside the lumped tax, so printing them here would bill it twice.
    const mgmtSplit = splitEven(splitTrusted ? (Number(fd.mgmtFee) || 0) : 0, paxRows.length);
    const mgmtTaxSplit = splitEven(splitTrusted ? (Number(fd.mgmtGst?.total) || 0) : 0, paxRows.length);

    // ---- Processing Fee, per passenger --------------------------------------
    // Our margin reaches the invoice as ONE booking-level scalar (fd.markup), and
    // printing it whole on the first row reads as though that one passenger had
    // been charged all of it. The per-TYPE margin is recoverable because
    // marginRuleService.markupPrice matches rules on `passenger_type` and the
    // marked-up price survives into the stored review copy as perPax[].fare:
    //     markup(type) = fare − base − taxes − count × (MF + MFT per head)
    // That subtraction is only meaningful for the STORED review copy, so it is
    // used ONLY when it reconciles with the authoritative scalar; otherwise we
    // apportion by ticket value rather than print a fabricated 0.00 against real
    // passengers.
    const paxTotal = paxRows.length;
    const mfPerHead = paxTotal > 0
      ? ((Number(fd.mgmtFee) || 0) + (Number(fd.mgmtGst?.total) || 0)) / paxTotal
      : 0;
    const countOfType = {};
    const markupOfType = {};
    perPax.forEach((pp) => {
      const type = pp.type || 'ADULT';
      const count = Math.max(1, Number(pp.count) || 1);
      const supplierSide = (Number(pp.base) || 0) + (Number(pp.taxes) || 0) + count * mfPerHead;
      countOfType[type] = count;
      markupOfType[type] = Math.max(0, r2m((Number(pp.fare) || 0) - supplierSide));
    });
    const derivedMarkup = r2m(Object.values(markupOfType).reduce((s, v) => s + v, 0));
    // ≤ ₹1 tolerance absorbs the per-head MF/MFT rounding, nothing more.
    const markupTrusted = processing > 0 && Math.abs(derivedMarkup - processing) <= 1;
    const markupWeights = paxRows.map((r) => (markupTrusted
      ? (markupOfType[r.type] || 0) / Math.max(1, countOfType[r.type] || 1)
      : r.base + r.ot + r.k3 + r.yq));
    // splitByWeight distributes the AUTHORITATIVE total, so the column still sums
    // to exactly fd.markup whichever weighting was used.
    const markupSplit = splitByWeight(processing, markupWeights);
    // The gateway transaction fee is levied on the BOOKING and carries no
    // per-passenger dimension, so it is shared equally rather than weighted.
    const txnSplit = splitEven(txnFee, paxTotal);
    // Convenience fee likewise: the server computes it from the booking total, so
    // there is no per-passenger truth to recover — an equal share is honest.
    const convSplit = splitEven(convFee, paxTotal);
    // A reschedule charge is levied on the BOOKING, so it shares equally too.
    const reissueSplit = splitEven(reissueCharge, paxTotal);

    // Travel insurance is NOT itemised here: it is billed on its own INSURANCE
    // INVOICE below this one (FlightInsuranceInvoiceDoc), which splits the premium
    // per insured traveller from the insurer's own policy.travellers[]. This table
    // and its total therefore cover the TICKET alone.

    rows = paxRows.map((r, i) => ({
      ticketNo: r.ticketNo,
      pnr: r.pnr,
      sectors, flights, travelDate,
      pax: r.pax,
      ff: r.ff || [],
      base: r.base, ot: r.ot, k3: r.k3, yq: r.yq,
      baggage: bagSplit[i] ?? 0,
      meal: mealSplit[i] ?? 0,
      seat: seatSplit[i] ?? 0,
      management: mgmtSplit[i] ?? 0,
      managementTax: mgmtTaxSplit[i] ?? 0,
      processing: r2m((markupSplit[i] ?? 0) + (txnSplit[i] ?? 0)),
      convenience: convSplit[i] ?? 0,
      // Booking-level, like the fees above — shared equally so a per-passenger
      // row never reads as though one traveller bore the whole reschedule.
      reissue: reissueSplit[i] ?? 0,
      // Kept on the row for the older no-fareDetail branch below to mirror, but
      // NOT printed as a column any more — the discount is booking-level and now
      // appears in the summary box under the total. See the note by `columns`.
      discount: i === 0 ? discount : 0,
    }));
    gstRow = splitTrusted
      ? { cgst: Number(fd.mgmtGst?.cgst) || 0, sgst: Number(fd.mgmtGst?.sgst) || 0, igst: 0 }
      : { cgst: 0, sgst: 0, igst: 0 };
  } else {
    // Older bookings with no stored fareDetail — honest two-column split. Every
    // charge column is 0 here (nothing itemises them) and the whole residual sits
    // in OT Tax, so the line still adds up to the Total Amount.
    const base = Number(b.fare?.base_fare) || 0;
    rows = [{
      ticketNo: 'NA',
      pnr: allPnrs.join(', ') || 'NA',
      sectors, flights, travelDate,
      pax: paxAll.map((p) => `${p.title ? `${p.title}. ` : ''}${p.firstName || ''} ${p.lastName || ''}`.replace(/\s+/g, ' ').trim())
        .filter(Boolean).join(', ') || '—',
      base, ot: Math.max(0, total - base), k3: 0, yq: 0,
      baggage: 0, meal: 0, seat: 0, management: 0, managementTax: 0,
      processing: 0, convenience: 0, reissue: 0, discount: 0,
    }];
  }

  // GST the booking was billed under (business input-tax credit). Absent unless
  // the traveller actually supplied a GSTIN. ONE row only — the lead passenger —
  // matching the supplier's invoice: a booking carries a SINGLE GSTIN, so
  // repeating it under every traveller read as though each had filed their own.
  const paxGst = b.gst_info && b.gst_info.gstNumber ? b.gst_info : null;
  const gstPaxNames = paxGst
    ? [paxAll.length
      ? (() => {
        const p = paxAll[0];
        const t = String(p.type || 'ADULT').toUpperCase()[0];
        return `${p.title ? `${p.title}. ` : ''}${p.firstName || ''} ${p.lastName || ''}`.trim().toUpperCase()
          + (t ? ` (${t})` : '');
      })()
      : '—']
    : [];

  // Flight invoice columns — the fixed identity/fare set, then charge columns
  // that appear ONLY when actually charged (like the reference invoice, which
  // omits empty columns).
  const anyPos = (key) => rows.some((r) => Number(r[key]) > 0);
  const columns = [
    { key: 'sno', label: 'S No.', w: 2.5, cell: (r, i) => i + 1 },
    { key: 'ticketNo', label: 'Ticket No.', w: 4.5, cell: (r) => r.ticketNo },
    { key: 'pnr', label: 'PNR No.', w: 4, cell: (r) => r.pnr },
    { key: 'sectors', label: 'Sectors', w: 4.5, cell: (r) => r.sectors },
    { key: 'flights', label: 'Flight', w: 4, cell: (r) => r.flights },
    { key: 'pax', label: 'PAX Name', w: 4.5, cell: (r) => (
      <>
        {r.pax}
        {(r.ff || []).map((l, k) => (
          <span key={k} style={{ display: 'block', fontSize: '0.85em', color: '#64748b' }}>FF: {l}</span>
        ))}
      </>
    ) },
    // One line ("25 Sept 2026, 15:25" needs ~72px at 8px): nowrap and sized for
    // it, the width taken from columns that CAN wrap — never from a money column.
    { key: 'travelDate', label: 'Travel Date', w: 9, nowrap: true, cell: (r) => r.travelDate },
    // Base Fare INCLUDES this passenger's share of our processing fee — it has no
    // column of its own. The row values are untouched, so Gross still adds up.
    { key: 'base', label: 'Base Fare', w: 6, num: true, cell: (r) => n2((Number(r.base) || 0) + (Number(r.processing) || 0)) },
    // "OT Tax" is a specific airline code. When the code split could not be trusted
  // this column carries the whole recorded tax instead, so it says so rather than
  // attributing every rupee of it to OT.
  { key: 'ot', label: taxSplitTrusted ? 'OT Tax' : 'Taxes and Fees', w: 6, num: true, cell: (r) => n2(r.ot) },
    { key: 'k3', label: 'K3/GST', w: 6, num: true, cell: (r) => n2(r.k3) },
    { key: 'yq', label: 'YQ Tax', w: 6, num: true, cell: (r) => n2(r.yq) },
    // Reference charge columns — always shown, so the ticket columns add up.
    { key: 'baggage', label: 'Baggage Charges', w: 6, num: true, cell: (r) => n2(r.baggage) },
    { key: 'meal', label: 'Meal Charges', w: 6, num: true, cell: (r) => n2(r.meal) },
    { key: 'seat', label: 'Seat Charges', w: 6, num: true, cell: (r) => n2(r.seat) },
    { key: 'management', label: 'Management Fee', w: 6.5, num: true, cell: (r) => n2(r.management) },
    { key: 'managementTax', label: 'Management Fee Tax', w: 6.5, num: true, cell: (r) => n2(r.managementTax) },
    // No "Insurance Fees" column: the travel-insurance premium is billed on its
    // own document below (see rows above).
    // Our own charges — shown only when actually levied.
    // In the place the Processing Fee column used to take — shown even at 0.00.
    { key: 'convenience', label: b?.fare?.convenience_label || 'Convenience Fee', w: 6.5, num: true, cell: (r) => n2(r.convenience) },
    // A rescheduled ticket's charge. It is part of what was paid, so without a
    // column of its own the table could not add up to the Total beneath it — and
    // the shortfall was invisible, because it surfaced as a NEGATIVE "discount"
    // that `Math.max(0, …)` discarded. Conditional, like the two above.
    ...(anyPos('reissue') ? [{ key: 'reissue', label: 'Reschedule Charge', w: 6.5, num: true, cell: (r) => n2(r.reissue) }] : []),
  ];
  // Normalised to 100% — Processing / Convenience are CONDITIONAL, so literal
  // widths that sum to 100 for one column set cannot sum to 100 for another.
  // They summed to 131, and `table-layout: fixed` then rescaled every column to
  // ~76% of its intended share. hotel/InvoiceDoc.jsx has always done this.
  const wSum = columns.reduce((a, c) => a + c.w, 0) || 1;
  const colWidths = columns.map((c) => Math.round((c.w / wSum) * 10000) / 100);


  // ---- invoice summary: Amount → Discount → Total to be Paid ------------------
  // Matches the customer storefront invoice exactly (booking_frontends/*/src/pages/
  // Invoice/index.jsx, 2026-09-08). Discount used to be a per-passenger COLUMN
  // carrying the whole booking-level figure on row 1, so a 2-pax ticket read as if
  // one traveller got the entire discount — and the printed figure disagreed with
  // the reduction actually applied, because the stored flightDiscount +
  // couponDiscount can count the same coupon twice.
  //
  // Both figures are DERIVED from what this invoice prints and what was actually
  // charged, never from those stored fields:
  //     Amount   = sum of every charge column in the table above (pre-discount)
  //     Discount = Amount − Total Paid
  //     Payable  = Total Paid   (fare.total_paid — the authoritative settled amount)
  // so the three lines always reconcile with each other AND with the table.
  // Measured against flightTotal (total_paid LESS any separately-invoiced
  // insurance premium), so the discount is the same figure it was when insurance
  // was a column here — and the flight document still reconciles with its own
  // table. `insurance` is gone from the keys for the same reason.
  const CHARGE_KEYS = ['base', 'ot', 'k3', 'yq', 'baggage', 'meal', 'seat',
    'management', 'managementTax', 'processing', 'convenience', 'reissue'];
  const grossAmount = r2m(rows.reduce(
    (sum, r) => sum + CHARGE_KEYS.reduce((a, k) => a + (Number(r[k]) || 0), 0),
    0,
  ));
  // The RECORDED discount, not the leftover. Deriving it as `gross − total` made
  // every unexplained rupee print as a reduction the customer never received, and
  // `Math.max(0, …)` then threw away a NEGATIVE gap entirely — so a rescheduled
  // booking's columns quietly fell short of the Total beneath them with nothing on
  // the page to say so. The derived form survives only for a row whose ledger does
  // not reconcile, where it is still the only way to make the document add up.
  const discountApplied = svc && svc.reconciled
    ? r2m(svc.discount)
    : Math.max(0, r2m(grossAmount - flightTotal));
  // Sub-paise noise is not a discount. Matches the old column's "only when > 0".
  const showDiscount = discountApplied >= 0.005;

  const paymentSplit = {
    walletPaid: Number(b.fare?.wallet_paid) || 0,
    gatewayPaid: Number(b.fare?.gateway_paid) || 0,
  };

  return (
    <>
      <div className="doc-bar">
        <button className="btn ghost" onClick={() => navigate(backToList(search, '/flight/bookings', '/flight/corporate-bookings'))}>
          <Icon name="chev" style={{ transform: 'rotate(180deg)' }} /> Back to Bookings
        </button>
        <div className="doc-bar-actions">
          {/* A booking sold with travel insurance prints TWO documents; these give
              each one on its own, the primary button prints both. */}
          {hasInsInvoice && (
            <>
              <button className="btn ghost" onClick={() => setPrintOnly('flight')}>
                <Icon name="download" /> Flight Invoice
              </button>
              <button className="btn ghost" onClick={() => setPrintOnly('insurance')}>
                <Icon name="download" /> Insurance Invoice
              </button>
            </>
          )}
          <button className="btn primary" onClick={() => window.print()}>
            <Icon name="download" /> {hasInsInvoice ? 'Print / Save Both' : 'Print / Save as PDF'}
          </button>
        </div>
      </div>

      <article className={`doc-sheet${printOnly === 'insurance' ? ' doc-print-hide' : ''}`}>
        {/* Brand mark top-left, above the centred INVOICE title — the same
            lockup the e-ticket / voucher prints, so every document issued for
            one booking reads as the same family. DocBrandMark owns the
            storefront → tenant → bundled-wordmark fallback chain. */}
        <DocBrandMark brandCode={b.brand} className="doc-brand-logo" />
        {/* Titled FLIGHT INVOICE only when there is a second document to tell it
            apart from — a booking without insurance keeps the plain INVOICE it
            has always printed. Same rule as the customer's copy. */}
        <h1 className="doc-title">{hasInsInvoice ? 'FLIGHT INVOICE' : 'INVOICE'}</h1>

        <div className="doc-meta">
          <p>No.: {ref}</p>
          <p className="doc-invdate">Invoice Date: {invoiceDate}</p>
        </div>
        <p className="doc-invno">Invoice No.: {invoiceNo}</p>

        <hr className="doc-hr" />

        <PartiesBlock b={b} company={COMPANY} />

        {/* .doc-table-wrap scrolls on a narrow console window instead of crushing
            18 columns; print and the html2canvas capture both reset it. */}
        <div className="doc-table-wrap">
          <table className="doc-table doc-table-wide">
            <colgroup>{colWidths.map((w, i) => <col key={columns[i].key} style={{ width: `${w}%` }} />)}</colgroup>
            <thead><tr>{columns.map((c) => <th key={c.key} className={c.num ? 'doc-num' : undefined}>{c.label}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  {columns.map((c) => <td key={c.key} className={c.num ? 'doc-num' : (c.nowrap ? 'doc-nowrap' : undefined)}>{c.cell(r, i)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Passenger GST Details — the GSTIN the traveller gave at checkout, so a
            business traveller can claim input-tax credit. Rendered ONLY when the
            booking actually carries one, with the same six columns as the
            supplier's own invoice and a SINGLE row for the lead passenger. */}
        {paxGst && (
          <div className="doc-gst-block">
            <p className="doc-h">Passenger GST Details :</p>
            <table className="doc-table doc-gst">
              <thead>
                <tr>
                  <th>Pax Name</th>
                  <th>GST Number</th>
                  <th>GST Company Phone</th>
                  <th>GST Company Address</th>
                  <th>GST Company Email</th>
                  <th>GST Company Name</th>
                </tr>
              </thead>
              <tbody>
                {gstPaxNames.map((nm, i) => (
                  <tr key={`${nm}-${i}`}>
                    <td>{nm}</td>
                    <td>{paxGst.gstNumber}</td>
                    <td>{paxGst.mobile || '—'}</td>
                    <td>{paxGst.address || '—'}</td>
                    <td>{paxGst.email || '—'}</td>
                    <td>{paxGst.registeredName || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="doc-summary">
          {/* When the per-code split could not be trusted there is no management-fee
              GST to state: the server withheld the split and its money is inside the
              lumped tax line, so this table would assert "CGST 0.00 / SGST 0.00"
              against GST that WAS collected — a false statement on a tax document,
              which is precisely what the insurance invoice already guards against. A
              rescheduled booking hits it on every render: the reissue supersedes the
              snapshot with the NEW fare while `base_fare` / `gst_amount` keep the
              original's, so the two can never agree. */}
          {!taxSplitTrusted ? <div /> : (
          <div>
            <p className="doc-h">GST Details :</p>
            <table className="doc-table doc-gst">
              <thead><tr><th>Description</th><th>CGST @9%</th><th>SGST @9%</th><th>IGST @18%</th></tr></thead>
              <tbody>
                <tr>
                  <td>Management Fee GST</td>
                  <td>{n2(gstRow.cgst)}</td>
                  <td>{n2(gstRow.sgst)}</td>
                  <td>{n2(gstRow.igst)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          )}
          {/* Amount → Less: Discount → Total Amount to be Paid, same as the
              customer invoice. ONE two-column grid rather than a flex row per
              line, so every amount shares a single right edge and the labels a
              single left edge — that is what keeps the three lines aligned both
              ways whatever the label lengths.
              With no discount it collapses to the single Total Amount line the
              reseller-commission block was reduced to in CR E8. */}
          {showDiscount ? (
            <div className="doc-totals doc-totals-grid">
              <span className="lbl">Amount</span>
              <span className="val">{rupee(grossAmount)}</span>
              <span className="lbl">Less: {b?.fare?.discount_label || 'Discount'}</span>
              <span className="val">- {rupee(discountApplied)}</span>
              <span className="lbl sum">Total Amount to be Paid</span>
              <span className="val sum">{rupee(flightTotal)}</span>
            </div>
          ) : (
            <div className="doc-totals doc-total-only">
              <div className="doc-trow doc-net"><b>Total Amount</b><b>{rupee(flightTotal)}</b></div>
            </div>
          )}
          {/* How the total was actually settled — wallet and/or gateway. */}
          <DocPaymentSplit split={paymentSplit} />
        </div>

        <hr className="doc-hr" />

        <div className="doc-terms">
          <p className="doc-h">Terms &amp; Conditions :</p>
          <ul>
            <li>All cases &amp; disputes are subject to {COMPANY.jurisdiction} jurisdiction only.</li>
            <li>Refunds &amp; cancellations are subject to airline approval.</li>
            <li>For payments made via a payment gateway (link) or using a credit card, additional gateway charges may apply as per the card / payment mode used.</li>
            {/* Tenant-configured terms (Tax Management), one <li> per line. */}
            {extraTerms(COMPANY.terms).map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </div>

        {/* The premium is billed on the insurance invoice overleaf, so state the
            reconciliation on the document that no longer carries it — otherwise
            this total simply disagrees with what the customer was charged. */}
        {hasInsInvoice && (
          <p className="doc-foot">
            Travel insurance of {rupee(insAmt)} is billed on a separate insurance invoice;
            together the two documents total {rupee(total)}.
          </p>
        )}

        <p className="doc-foot">This is a Computer Generated Invoice, No Signature required.</p>
      </article>

      {/* The insurance invoice, its own sheet under the flight one. breakBefore
          gives it its own page when BOTH print; ignored on screen and when one is
          printed alone. */}
      {hasInsInvoice && (
        <article
          className={`doc-sheet${printOnly === 'flight' ? ' doc-print-hide' : ''}`}
          style={{ breakBefore: printOnly ? 'auto' : 'page' }}
        >
          <FlightInsuranceInvoiceDoc
            b={b}
            company={COMPANY}
            amount={insAmt}
            flightInvoiceNo={invoiceNo}
            invoiceDate={invoiceDate}
          />
        </article>
      )}
    </>
  );
}
